/**
 * DeskeyChart — Lucidchart-style flowchart / diagram editor
 * Build flowcharts, org charts, decision trees, mind maps with smart connectors.
 * - Shape palette (click shape → click canvas to place)
 * - Drag to move nodes, connectors auto-follow
 * - Smart connectors: 4 edge anchors on hover; drag from anchor → another shape
 * - Inline text editing (double-click)
 * - Per-shape style + theme presets
 * - 5 templates
 * - Undo/Redo · Delete · Save as PNG
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Circle, Ellipse, Line, Arrow, RegularPolygon, Text, Transformer, Group, Path } from 'react-konva';
import useCollabRoom from '../hooks/useCollabRoom';
import CollabBadge from './CollabBadge';
import { friendlyError } from '../lib/errors';

// ─── Shape catalog ─────────────────────────────────────────────────────────
const SHAPE_CATALOG = [
  { cat: 'Flowchart', items: [
    { id: 'process', label: 'Process', icon: '▭' },
    { id: 'decision', label: 'Decision', icon: '◇' },
    { id: 'terminator', label: 'Start/End', icon: '⬭' },
    { id: 'data', label: 'Data', icon: '▱' },
    { id: 'document', label: 'Document', icon: '📄' },
    { id: 'database', label: 'Database', icon: '⌗' },
    { id: 'manual', label: 'Manual Input', icon: '⏢' },
    { id: 'predefined', label: 'Predefined', icon: '⎕' },
    { id: 'connector', label: 'Connector', icon: '○' },
  ]},
  { cat: 'Basic', items: [
    { id: 'rect', label: 'Rectangle', icon: '□' },
    { id: 'roundRect', label: 'Rounded', icon: '▢' },
    { id: 'circle', label: 'Circle', icon: '●' },
    { id: 'ellipse', label: 'Ellipse', icon: '⬭' },
    { id: 'triangle', label: 'Triangle', icon: '▲' },
    { id: 'hexagon', label: 'Hexagon', icon: '⬡' },
    { id: 'cloud', label: 'Cloud', icon: '☁' },
    { id: 'note', label: 'Note', icon: '🗒' },
  ]},
];

const THEMES = {
  emerald: { fill: '#ecfdf5', stroke: '#34A853', text: '#065f46' },
  sapphire: { fill: '#eff6ff', stroke: '#3b82f6', text: '#1e3a8a' },
  amber: { fill: '#fffbeb', stroke: '#d97706', text: '#78350f' },
  slate: { fill: '#f1f5f9', stroke: '#475569', text: '#0f172a' },
  rose: { fill: '#fff1f2', stroke: '#e11d48', text: '#881337' },
};

const DEFAULT_NODE = {
  width: 140, height: 70,
  fill: '#ecfdf5', stroke: '#34A853', strokeWidth: 2, textColor: '#065f46', fontSize: 14,
};

// ─── Templates ─────────────────────────────────────────────────────────────
const uid = () => `n${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
const mkNode = (type, x, y, text, w = 140, h = 70, theme = 'emerald') => ({
  id: uid(), type, x, y, width: w, height: h, text,
  fill: THEMES[theme].fill, stroke: THEMES[theme].stroke, textColor: THEMES[theme].text,
  strokeWidth: 2, fontSize: 14,
});
const mkConn = (from, to, fromSide = 'bottom', toSide = 'top', label = '') => ({
  id: uid(), from, to, fromSide, toSide, label,
});

const TEMPLATES = [
  { id: 'flowchart', label: 'Basic Flowchart', build: () => {
    const start = mkNode('terminator', 380, 60, 'Start', 140, 55);
    const proc1 = mkNode('process', 380, 160, 'Do something');
    const dec = mkNode('decision', 380, 280, 'Decision?', 160, 90);
    const yes = mkNode('process', 220, 420, 'Yes path');
    const no = mkNode('process', 560, 420, 'No path');
    const end = mkNode('terminator', 380, 540, 'End', 140, 55);
    return {
      nodes: [start, proc1, dec, yes, no, end],
      connectors: [mkConn(start.id, proc1.id), mkConn(proc1.id, dec.id),
        mkConn(dec.id, yes.id, 'left', 'top', 'Yes'), mkConn(dec.id, no.id, 'right', 'top', 'No'),
        mkConn(yes.id, end.id, 'bottom', 'left'), mkConn(no.id, end.id, 'bottom', 'right')],
    };
  }},
  { id: 'decision', label: 'Decision Tree', build: () => {
    const root = mkNode('decision', 400, 50, 'Is condition true?', 200, 80, 'sapphire');
    const yes = mkNode('decision', 180, 200, 'Yes branch?', 160, 70, 'sapphire');
    const no = mkNode('decision', 620, 200, 'No branch?', 160, 70, 'sapphire');
    const a = mkNode('process', 60, 350, 'Action A', 120, 55);
    const b = mkNode('process', 260, 350, 'Action B', 120, 55);
    const c = mkNode('process', 500, 350, 'Action C', 120, 55);
    const d = mkNode('process', 700, 350, 'Action D', 120, 55);
    return {
      nodes: [root, yes, no, a, b, c, d],
      connectors: [mkConn(root.id, yes.id, 'left', 'top', 'Yes'), mkConn(root.id, no.id, 'right', 'top', 'No'),
        mkConn(yes.id, a.id, 'left', 'top'), mkConn(yes.id, b.id, 'right', 'top'),
        mkConn(no.id, c.id, 'left', 'top'), mkConn(no.id, d.id, 'right', 'top')],
    };
  }},
  { id: 'org', label: 'Org Chart', build: () => {
    const ceo = mkNode('rect', 400, 50, 'CEO', 160, 60, 'slate');
    const vp1 = mkNode('rect', 180, 200, 'VP Engineering', 160, 55, 'slate');
    const vp2 = mkNode('rect', 400, 200, 'VP Product', 160, 55, 'slate');
    const vp3 = mkNode('rect', 620, 200, 'VP Sales', 160, 55, 'slate');
    const r1 = mkNode('rect', 80, 340, 'Team Lead', 140, 50, 'slate');
    const r2 = mkNode('rect', 280, 340, 'Team Lead', 140, 50, 'slate');
    const r3 = mkNode('rect', 490, 340, 'PM', 140, 50, 'slate');
    const r4 = mkNode('rect', 650, 340, 'Account Exec', 140, 50, 'slate');
    return { nodes: [ceo, vp1, vp2, vp3, r1, r2, r3, r4],
      connectors: [mkConn(ceo.id, vp1.id), mkConn(ceo.id, vp2.id), mkConn(ceo.id, vp3.id),
        mkConn(vp1.id, r1.id), mkConn(vp1.id, r2.id), mkConn(vp2.id, r3.id), mkConn(vp3.id, r4.id)],
    };
  }},
  { id: 'process', label: 'Process Flow', build: () => {
    const s = [
      mkNode('terminator', 40, 200, 'Start', 100, 55, 'amber'),
      mkNode('process', 200, 200, 'Step 1', 140, 55, 'amber'),
      mkNode('process', 400, 200, 'Step 2', 140, 55, 'amber'),
      mkNode('process', 600, 200, 'Step 3', 140, 55, 'amber'),
      mkNode('terminator', 800, 200, 'Done', 100, 55, 'amber'),
    ];
    return { nodes: s, connectors: [mkConn(s[0].id, s[1].id, 'right', 'left'), mkConn(s[1].id, s[2].id, 'right', 'left'), mkConn(s[2].id, s[3].id, 'right', 'left'), mkConn(s[3].id, s[4].id, 'right', 'left')] };
  }},
  { id: 'mind', label: 'Mind Map', build: () => {
    const c = mkNode('ellipse', 400, 240, 'Central Idea', 160, 70, 'rose');
    const b1 = mkNode('roundRect', 120, 100, 'Topic 1', 140, 50, 'rose');
    const b2 = mkNode('roundRect', 580, 100, 'Topic 2', 140, 50, 'rose');
    const b3 = mkNode('roundRect', 60, 260, 'Topic 3', 140, 50, 'rose');
    const b4 = mkNode('roundRect', 640, 260, 'Topic 4', 140, 50, 'rose');
    const b5 = mkNode('roundRect', 120, 420, 'Topic 5', 140, 50, 'rose');
    const b6 = mkNode('roundRect', 580, 420, 'Topic 6', 140, 50, 'rose');
    return { nodes: [c, b1, b2, b3, b4, b5, b6],
      connectors: [mkConn(c.id, b1.id, 'top', 'right'), mkConn(c.id, b2.id, 'top', 'left'),
        mkConn(c.id, b3.id, 'left', 'right'), mkConn(c.id, b4.id, 'right', 'left'),
        mkConn(c.id, b5.id, 'bottom', 'right'), mkConn(c.id, b6.id, 'bottom', 'left')] };
  }},
];

// ─── Side anchors (N/E/S/W) ─────────────────────────────────────────────────
const SIDE_POS = (node, side) => {
  const cx = node.x + node.width / 2, cy = node.y + node.height / 2;
  if (side === 'top') return { x: cx, y: node.y };
  if (side === 'bottom') return { x: cx, y: node.y + node.height };
  if (side === 'left') return { x: node.x, y: cy };
  if (side === 'right') return { x: node.x + node.width, y: cy };
  return { x: cx, y: cy };
};
// Guess the best side of a target node given a start point
const BEST_SIDE = (node, from) => {
  const cx = node.x + node.width / 2, cy = node.y + node.height / 2;
  const dx = from.x - cx, dy = from.y - cy;
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'bottom' : 'top';
};

export default function DeskeyChart({ onSave, onClose, initialNodes, initialConnectors, isEditing = false }) {
  const stageRef = useRef(null);
  const trRef = useRef(null);
  const textAreaRef = useRef(null);
  const [nodes, setNodes] = useState(initialNodes || []);
  const [connectors, setConnectors] = useState(initialConnectors || []);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedConnId, setSelectedConnId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [placingShape, setPlacingShape] = useState(null);        // { type } – next click drops this
  const [drawingConn, setDrawingConn] = useState(null);          // { fromId, fromSide, x2, y2 }
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [snap, setSnap] = useState(true);
  const [theme, setTheme] = useState('emerald');
  const [hist, setHist] = useState([{ nodes: initialNodes || [], connectors: initialConnectors || [] }]);
  const [histIdx, setHistIdx] = useState(0);
  const [showTemplates, setShowTemplates] = useState(!isEditing && !(initialNodes && initialNodes.length > 0));
  const [saving, setSaving] = useState(false);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  // ── Realtime collab — same protocol as DeskeyPaint: a 'sync' event with
  //    the full {nodes, connectors} object whenever local state changes,
  //    throttled to ~150ms. Peers replace local state on receive.
  const [collabRoom, setCollabRoom] = useState(() => {
    try { return new URL(window.location.href).searchParams.get('collab_room') || null; }
    catch (_e) { return null; }
  });
  const lastSyncRef = useRef(0);
  const handleCollabMessage = useCallback((m) => {
    if (m.kind !== 'sync') return;
    if (Array.isArray(m.nodes)) setNodes(m.nodes);
    if (Array.isArray(m.connectors)) setConnectors(m.connectors);
  }, []);
  const { send: collabSend, peerCount, status: collabStatus } =
    useCollabRoom(collabRoom, { onMessage: handleCollabMessage });
  useEffect(() => {
    if (!collabRoom) return;
    const now = Date.now();
    if (now - lastSyncRef.current < 150) return;
    lastSyncRef.current = now;
    collabSend({ kind: 'sync', nodes, connectors });
  }, [nodes, connectors, collabRoom, collabSend]);
  const startCollab = () => {
    const id = collabRoom || ('chart-' + Math.random().toString(36).slice(2, 10));
    setCollabRoom(id);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('collab_room', id);
      window.history.replaceState({}, '', url.toString());
    } catch (_e) { /* ignore */ }
  };
  const CW = 1200, CH = 720;
  const GRID = 20;

  // History
  const snapshot = useCallback((n, c) => {
    const snap = { nodes: JSON.parse(JSON.stringify(n)), connectors: JSON.parse(JSON.stringify(c)) };
    setHist(prev => [...prev.slice(0, histIdx + 1), snap].slice(-40));
    setHistIdx(prev => Math.min(prev + 1, 39));
  }, [histIdx]);
  const loadSnap = (s) => { setNodes(JSON.parse(JSON.stringify(s.nodes))); setConnectors(JSON.parse(JSON.stringify(s.connectors))); setSelectedId(null); setEditingId(null); };
  const undo = useCallback(() => { if (histIdx > 0) { setHistIdx(histIdx - 1); loadSnap(hist[histIdx - 1]); } }, [histIdx, hist]);
  const redo = useCallback(() => { if (histIdx < hist.length - 1) { setHistIdx(histIdx + 1); loadSnap(hist[histIdx + 1]); } }, [histIdx, hist]);

  // Key handling
  const r = useRef({});
  r.current = { selectedId, selectedConnId, editingId, nodes, connectors, undo, redo, snapshot };
  useEffect(() => {
    const fn = (e) => {
      if (r.current.editingId) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && (r.current.selectedId || r.current.selectedConnId)) {
        e.preventDefault();
        if (r.current.selectedId) {
          const nn = r.current.nodes.filter(n => n.id !== r.current.selectedId);
          const nc = r.current.connectors.filter(c => c.from !== r.current.selectedId && c.to !== r.current.selectedId);
          setNodes(nn); setConnectors(nc); r.current.snapshot(nn, nc); setSelectedId(null);
        } else if (r.current.selectedConnId) {
          const nc = r.current.connectors.filter(c => c.id !== r.current.selectedConnId);
          setConnectors(nc); r.current.snapshot(r.current.nodes, nc); setSelectedConnId(null);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); r.current.undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) { e.preventDefault(); r.current.redo(); }
      if (e.key === 'Escape') { setPlacingShape(null); setDrawingConn(null); setSelectedId(null); setSelectedConnId(null); }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  // Transformer binding
  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;
    if (selectedId && !editingId) {
      const n = stageRef.current.findOne('#' + selectedId);
      if (n) { trRef.current.nodes([n]); trRef.current.getLayer()?.batchDraw(); }
      else trRef.current.nodes([]);
    } else trRef.current.nodes([]);
  }, [selectedId, editingId, nodes]);

  // Template loader
  const loadTemplate = (tpl) => {
    const built = tpl.build();
    setNodes(built.nodes); setConnectors(built.connectors);
    snapshot(built.nodes, built.connectors);
    setShowTemplates(false); setSelectedId(null); setSelectedConnId(null);
  };

  const blankStart = () => { setShowTemplates(false); snapshot([], []); };

  const sn = (v) => snap ? Math.round(v / GRID) * GRID : v;
  const getPos = () => {
    const p = stageRef.current?.getRelativePointerPosition();
    return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
  };

  // Stage handlers
  const onStageDown = (e) => {
    const target = e.target;
    const stage = target.getStage();
    const isBg = target === stage || target.attrs?.name === 'bg';
    const pos = getPos();

    if (placingShape) {
      const th = THEMES[theme];
      const node = {
        id: uid(), type: placingShape,
        x: sn(pos.x - DEFAULT_NODE.width / 2), y: sn(pos.y - DEFAULT_NODE.height / 2),
        width: DEFAULT_NODE.width, height: DEFAULT_NODE.height,
        text: placingShape === 'note' ? 'Note…' : placingShape.charAt(0).toUpperCase() + placingShape.slice(1),
        fill: th.fill, stroke: th.stroke, textColor: th.text, strokeWidth: 2, fontSize: 14,
      };
      const nn = [...nodes, node];
      setNodes(nn); snapshot(nn, connectors);
      setSelectedId(node.id); setPlacingShape(null);
      return;
    }

    if (isBg) {
      if (editingId) commitTextEdit();
      setSelectedId(null); setSelectedConnId(null);
    }
  };
  const onStageMove = () => {
    const pos = getPos();
    setCursor({ x: Math.round(pos.x), y: Math.round(pos.y) });
    if (drawingConn) { setDrawingConn(d => ({ ...d, x2: pos.x, y2: pos.y })); }
  };
  const onStageUp = (e) => {
    if (drawingConn) {
      const target = e.target;
      // see if we're on a node
      let hitNodeId = null;
      let p = target;
      while (p && p !== stageRef.current) {
        if (p.attrs?.name === 'node-group') { hitNodeId = p.attrs.id; break; }
        p = p.getParent?.();
      }
      if (hitNodeId && hitNodeId !== drawingConn.fromId) {
        const targetNode = nodes.find(n => n.id === hitNodeId);
        const fromNode = nodes.find(n => n.id === drawingConn.fromId);
        const fromP = SIDE_POS(fromNode, drawingConn.fromSide);
        const toSide = BEST_SIDE(targetNode, fromP);
        const nc = [...connectors, mkConn(drawingConn.fromId, hitNodeId, drawingConn.fromSide, toSide)];
        setConnectors(nc); snapshot(nodes, nc);
      }
      setDrawingConn(null);
    }
  };

  // Node drag
  const onNodeDragEnd = (id, e) => {
    const nx = sn(e.target.x()), ny = sn(e.target.y());
    const nn = nodes.map(n => n.id === id ? { ...n, x: nx, y: ny } : n);
    e.target.position({ x: nx, y: ny });
    setNodes(nn); snapshot(nn, connectors);
  };
  const onNodeDragMove = (id, e) => {
    const nx = e.target.x(), ny = e.target.y();
    setNodes(prev => prev.map(n => n.id === id ? { ...n, x: nx, y: ny } : n));
  };
  const onNodeTransformEnd = (id, e) => {
    const node = e.target;
    const nw = Math.max(40, node.width() * node.scaleX());
    const nh = Math.max(30, node.height() * node.scaleY());
    const nx = node.x(), ny = node.y();
    node.scaleX(1); node.scaleY(1); node.width(nw); node.height(nh);
    const nn = nodes.map(n => n.id === id ? { ...n, x: nx, y: ny, width: nw, height: nh } : n);
    setNodes(nn); snapshot(nn, connectors);
  };

  // Text editing
  const startTextEdit = (id) => { setEditingId(id); setSelectedId(id); };
  const commitTextEdit = () => { if (editingId) { snapshot(nodes, connectors); setEditingId(null); } };
  useEffect(() => { if (editingId && textAreaRef.current) { textAreaRef.current.focus(); textAreaRef.current.select(); } }, [editingId]);

  // Connector anchor drag
  const onAnchorDown = (nodeId, side, e) => {
    e.cancelBubble = true;
    const node = nodes.find(n => n.id === nodeId); if (!node) return;
    const p = SIDE_POS(node, side);
    setDrawingConn({ fromId: nodeId, fromSide: side, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  // Apply theme to selected node
  const applyTheme = (themeId) => {
    const th = THEMES[themeId];
    setTheme(themeId);
    if (selectedId) {
      const nn = nodes.map(n => n.id === selectedId ? { ...n, fill: th.fill, stroke: th.stroke, textColor: th.text } : n);
      setNodes(nn); snapshot(nn, connectors);
    }
  };
  const updateNodeProp = (prop, val) => {
    if (!selectedId) return;
    const nn = nodes.map(n => n.id === selectedId ? { ...n, [prop]: val } : n);
    setNodes(nn); snapshot(nn, connectors);
  };

  const deleteSelected = () => {
    if (selectedId) {
      const nn = nodes.filter(n => n.id !== selectedId);
      const nc = connectors.filter(c => c.from !== selectedId && c.to !== selectedId);
      setNodes(nn); setConnectors(nc); snapshot(nn, nc); setSelectedId(null);
    } else if (selectedConnId) {
      const nc = connectors.filter(c => c.id !== selectedConnId);
      setConnectors(nc); snapshot(nodes, nc); setSelectedConnId(null);
    }
  };
  const clearAll = () => { setNodes([]); setConnectors([]); setSelectedId(null); setSelectedConnId(null); snapshot([], []); };

  const handleSave = () => {
    setSaving(true); setSelectedId(null); setSelectedConnId(null); setEditingId(null);
    setTimeout(() => {
      try {
        const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2, width: CW, height: CH });
        onSave({ dataUrl, nodes, connectors, theme });
      } catch (er) { console.error(er); }
      setSaving(false);
    }, 300);
  };

  // ─── Node Shape Renderer ────────────────────────────────────────────────
  const renderNodeShape = (node, isSel) => {
    const { width: w, height: h, fill, stroke, strokeWidth: sw, type } = node;
    const selStroke = isSel ? '#3b82f6' : stroke;
    const selW = isSel ? Math.max(sw, 3) : sw;
    const common = { stroke: selStroke, strokeWidth: selW, fill };
    switch (type) {
      case 'process':
      case 'rect':
        return <Rect width={w} height={h} {...common} />;
      case 'roundRect':
        return <Rect width={w} height={h} cornerRadius={10} {...common} />;
      case 'decision':
        return <Line points={[w/2, 0, w, h/2, w/2, h, 0, h/2]} closed {...common} />;
      case 'terminator':
        return <Rect width={w} height={h} cornerRadius={h/2} {...common} />;
      case 'data':
        return <Line points={[w*0.15, 0, w, 0, w*0.85, h, 0, h]} closed {...common} />;
      case 'manual':
        return <Line points={[w*0.15, 0, w, 0, w, h, 0, h]} closed {...common} />;
      case 'document':
        return <Path data={`M0 0 L${w} 0 L${w} ${h*0.8} Q${w*0.75} ${h*1.1} ${w*0.5} ${h*0.8} Q${w*0.25} ${h*0.5} 0 ${h*0.8} Z`} {...common} />;
      case 'database':
        return (<Group>
          <Ellipse x={w/2} y={h*0.1} radiusX={w/2} radiusY={h*0.1} {...common} />
          <Rect x={0} y={h*0.1} width={w} height={h*0.8} fill={fill} stroke="transparent" />
          <Line points={[0, h*0.1, 0, h*0.9]} stroke={selStroke} strokeWidth={selW} />
          <Line points={[w, h*0.1, w, h*0.9]} stroke={selStroke} strokeWidth={selW} />
          <Ellipse x={w/2} y={h*0.9} radiusX={w/2} radiusY={h*0.1} {...common} />
        </Group>);
      case 'predefined':
        return (<Group>
          <Rect width={w} height={h} {...common} />
          <Line points={[10, 0, 10, h]} stroke={selStroke} strokeWidth={selW/2} />
          <Line points={[w-10, 0, w-10, h]} stroke={selStroke} strokeWidth={selW/2} />
        </Group>);
      case 'connector':
        return <Circle x={w/2} y={h/2} radius={Math.min(w, h)/2} {...common} />;
      case 'circle':
        return <Circle x={w/2} y={h/2} radius={Math.min(w, h)/2} {...common} />;
      case 'ellipse':
        return <Ellipse x={w/2} y={h/2} radiusX={w/2} radiusY={h/2} {...common} />;
      case 'triangle':
        return <RegularPolygon x={w/2} y={h/2} sides={3} radius={Math.min(w, h)/2} {...common} />;
      case 'hexagon':
        return <RegularPolygon x={w/2} y={h/2} sides={6} radius={Math.min(w, h)/2} {...common} />;
      case 'cloud': {
        const pts = [];
        const cx2 = w/2, cy2 = h/2, rx2 = w/2, ry2 = h/2;
        for (let i = 0; i < 12; i++) {
          const a = i*Math.PI*2/12, r2 = i % 2 === 0 ? 1 : 0.75;
          pts.push(cx2 + rx2*r2*Math.cos(a), cy2 + ry2*r2*Math.sin(a));
        }
        return <Line points={pts} closed tension={0.4} {...common} />;
      }
      case 'note':
        return <Path data={`M0 0 L${w-20} 0 L${w} 20 L${w} ${h} L0 ${h} Z M${w-20} 0 L${w-20} 20 L${w} 20`} {...common} />;
      default:
        return <Rect width={w} height={h} {...common} />;
    }
  };

  // ─── Connector rendering ────────────────────────────────────────────────
  // Offset parallel connectors (same from side on same node) so they don't stack on top of each other
  const connMetaRef = useRef({});
  useEffect(() => {
    // Group connectors by (from, fromSide) and (to, toSide); compute per-connector offset
    const fromGroups = {};
    const toGroups = {};
    connectors.forEach(c => {
      const fk = `${c.from}:${c.fromSide}`;
      const tk = `${c.to}:${c.toSide}`;
      (fromGroups[fk] = fromGroups[fk] || []).push(c.id);
      (toGroups[tk] = toGroups[tk] || []).push(c.id);
    });
    const meta = {};
    connectors.forEach(c => {
      const fk = `${c.from}:${c.fromSide}`;
      const tk = `${c.to}:${c.toSide}`;
      const fgroup = fromGroups[fk];
      const tgroup = toGroups[tk];
      const fIdx = fgroup.indexOf(c.id);
      const tIdx = tgroup.indexOf(c.id);
      // Shift by index in group so parallel lines fan out
      const fShift = fgroup.length > 1 ? (fIdx - (fgroup.length - 1) / 2) * 18 : 0;
      const tShift = tgroup.length > 1 ? (tIdx - (tgroup.length - 1) / 2) * 18 : 0;
      meta[c.id] = { fShift, tShift };
    });
    connMetaRef.current = meta;
  }, [connectors]);

  const renderConn = (c) => {
    const f = nodes.find(n => n.id === c.from), t = nodes.find(n => n.id === c.to);
    if (!f || !t) return null;
    const m = connMetaRef.current[c.id] || { fShift: 0, tShift: 0 };
    const fp = { ...SIDE_POS(f, c.fromSide) };
    const tp = { ...SIDE_POS(t, c.toSide) };
    // Apply shift perpendicular to side direction
    if (c.fromSide === 'top' || c.fromSide === 'bottom') fp.x += m.fShift; else fp.y += m.fShift;
    if (c.toSide === 'top' || c.toSide === 'bottom') tp.x += m.tShift; else tp.y += m.tShift;
    const isSel = selectedConnId === c.id;
    // orthogonal routing
    const offs = 28;
    let mid1 = { ...fp }, mid2 = { ...tp };
    if (c.fromSide === 'bottom') mid1 = { x: fp.x, y: fp.y + offs };
    else if (c.fromSide === 'top') mid1 = { x: fp.x, y: fp.y - offs };
    else if (c.fromSide === 'left') mid1 = { x: fp.x - offs, y: fp.y };
    else if (c.fromSide === 'right') mid1 = { x: fp.x + offs, y: fp.y };
    if (c.toSide === 'bottom') mid2 = { x: tp.x, y: tp.y + offs };
    else if (c.toSide === 'top') mid2 = { x: tp.x, y: tp.y - offs };
    else if (c.toSide === 'left') mid2 = { x: tp.x - offs, y: tp.y };
    else if (c.toSide === 'right') mid2 = { x: tp.x + offs, y: tp.y };
    const vert1 = c.fromSide === 'top' || c.fromSide === 'bottom';
    const corner = vert1 ? { x: mid2.x, y: mid1.y } : { x: mid1.x, y: mid2.y };
    const points = [fp.x, fp.y, mid1.x, mid1.y, corner.x, corner.y, mid2.x, mid2.y, tp.x, tp.y];
    const labelX = (fp.x + tp.x) / 2, labelY = (fp.y + tp.y) / 2 - 10;
    const color = isSel ? '#3b82f6' : '#475569';
    return (<Group key={c.id} onClick={(e) => { e.cancelBubble = true; setSelectedConnId(c.id); setSelectedId(null); }} onTap={(e) => { e.cancelBubble = true; setSelectedConnId(c.id); setSelectedId(null); }}>
      {/* Visible arrow */}
      <Arrow points={points} stroke={color} strokeWidth={isSel ? 3 : 2} fill={color} pointerLength={11} pointerWidth={11} hitStrokeWidth={28} lineJoin="round" />
      {c.label && (<Group x={labelX - 32} y={labelY - 11}>
        <Rect width={64} height={18} cornerRadius={4} fill={isSel ? '#dbeafe' : '#ffffff'} stroke={color} strokeWidth={1} opacity={0.95} />
        <Text text={c.label} width={64} height={18} fontSize={11} align="center" verticalAlign="middle" fill={color} fontStyle="500" />
      </Group>)}
    </Group>);
  };

  // Anchors for selected or hovered node — larger hit areas, visible halos
  const renderAnchors = (node, isHover) => {
    const sides = ['top', 'right', 'bottom', 'left'];
    return sides.map(s => {
      const p = SIDE_POS(node, s);
      return (<Group key={`a-${node.id}-${s}`} x={p.x} y={p.y}>
        {/* Large invisible hit zone so the user has ~20px radius to grab */}
        <Circle radius={20} fill="rgba(52,168,83,0.001)" onMouseDown={e => onAnchorDown(node.id, s, e)} onMouseEnter={e => { const st = e.target.getStage(); if (st) st.container().style.cursor = 'crosshair'; }} onMouseLeave={e => { const st = e.target.getStage(); if (st) st.container().style.cursor = 'default'; }} data-testid={`chart-anchor-${node.id}-${s}`} />
        {/* Soft outer glow */}
        <Circle radius={11} fill="rgba(52,168,83,0.18)" listening={false} />
        {/* Visible anchor */}
        <Circle radius={isHover ? 7 : 6} fill="#ffffff" stroke="#34A853" strokeWidth={2.5} listening={false} />
        {/* Inner dot */}
        <Circle radius={2} fill="#34A853" listening={false} />
      </Group>);
    });
  };

  // AI: generate chart from prompt
  const applyAIResult = (aiNodes, aiConns) => {
    // Normalize col/row → canvas coords
    const COL_W = 190, ROW_H = 130, LEFT = 80, TOP = 60;
    const NW = 160, NH = 70;
    const idMap = {};
    const built = aiNodes.map(n => {
      const local = uid();
      idMap[n.id] = local;
      const th = THEMES[theme];
      return {
        id: local, type: n.type, x: LEFT + (n.col || 0) * COL_W, y: TOP + (n.row || 0) * ROW_H,
        width: n.type === 'terminator' ? 130 : NW, height: n.type === 'terminator' ? 55 : NH,
        text: n.text || '',
        fill: th.fill, stroke: th.stroke, textColor: th.text, strokeWidth: 2, fontSize: 13,
      };
    });
    const conns = (aiConns || []).map(c => {
      const fromNode = built.find(nd => nd.id === idMap[c.from]);
      const toNode = built.find(nd => nd.id === idMap[c.to]);
      if (!fromNode || !toNode) return null;
      // choose natural sides based on relative position
      const dx = (toNode.x + toNode.width / 2) - (fromNode.x + fromNode.width / 2);
      const dy = (toNode.y + toNode.height / 2) - (fromNode.y + fromNode.height / 2);
      let fromSide, toSide;
      if (Math.abs(dx) > Math.abs(dy)) {
        fromSide = dx > 0 ? 'right' : 'left';
        toSide = dx > 0 ? 'left' : 'right';
      } else {
        fromSide = dy > 0 ? 'bottom' : 'top';
        toSide = dy > 0 ? 'top' : 'bottom';
      }
      return { id: uid(), from: idMap[c.from], to: idMap[c.to], fromSide, toSide, label: c.label || '' };
    }).filter(Boolean);
    setNodes(built); setConnectors(conns);
    snapshot(built, conns);
    setSelectedId(null); setSelectedConnId(null);
    setShowAIModal(false); setShowTemplates(false);
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) { setAiError('Please describe the diagram'); return; }
    setAiLoading(true); setAiError('');
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 90000);
      const res = await fetch(`${base}/api/teacher/studio/chart/ai-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: aiPrompt.trim(), max_nodes: 15 }),
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 504) throw new Error('AI service is busy — try a shorter prompt or retry in 10s');
        if (res.status === 502) throw new Error(err.detail || 'AI service temporarily unavailable — please retry');
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      if (!data.nodes || data.nodes.length === 0) throw new Error('AI returned no shapes');
      applyAIResult(data.nodes, data.connectors || []);
      setAiPrompt('');
    } catch (e) {
      const msg = e.name === 'AbortError' ? 'AI is busy right now — try again in a moment' : friendlyError(e, 'Generation failed');
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const selectedNode = nodes.find(n => n.id === selectedId);
  const selectedConn = connectors.find(c => c.id === selectedConnId);
  const editingNode = editingId ? nodes.find(n => n.id === editingId) : null;
  const legacyEmpty = isEditing && nodes.length === 0;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center" data-testid="chart-canvas-modal" onClick={onClose}>
      <div className="bg-[#f0f0f0] rounded-xl shadow-2xl w-[98vw] max-w-[1500px] h-[95vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Titlebar */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white border-b border-gray-200 flex-shrink-0">
          <span className="font-bold text-[#34A853] text-[14px]">Desky Chart</span>
          {collabRoom ? (
            <CollabBadge roomId={collabRoom} peerCount={peerCount} status={collabStatus} className="ml-1" />
          ) : (
            <button
              onClick={startCollab}
              data-testid="chart-start-collab"
              className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100">
              ● Go Live
            </button>
          )}
          <span className="text-[10px] text-gray-400">Flowchart · Diagrams · Mind Maps</span>
          {/* AI Generate button */}
          <button onClick={() => { setShowAIModal(true); setAiError(''); }} className="ml-3 text-[11px] font-bold text-white px-3 py-1.5 rounded-lg transition-all active:scale-95 flex items-center gap-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 shadow-sm" data-testid="chart-ai-btn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2l2.09 5.26L19 9.27l-4.55 3.96L16 19l-4-3-4 3 1.55-5.77L5 9.27l4.91-2.01L12 2z"/></svg>
            AI Generate
          </button>
          <div className="flex-1" />

          {/* Toolbar mini */}
          <button onClick={undo} className="text-gray-500 p-1.5 rounded hover:bg-gray-100" title="Undo (Ctrl+Z)" data-testid="chart-undo"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/></svg></button>
          <button onClick={redo} className="text-gray-500 p-1.5 rounded hover:bg-gray-100" title="Redo (Ctrl+Y)" data-testid="chart-redo"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 11-5.64-11.36L23 10"/></svg></button>
          <button onClick={() => setShowGrid(g => !g)} className={`text-[10px] font-bold px-2 py-1 rounded ${showGrid ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="chart-grid-toggle">Grid</button>
          <button onClick={() => setSnap(s => !s)} className={`text-[10px] font-bold px-2 py-1 rounded ${snap ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="chart-snap-toggle">Snap</button>
          <button onClick={clearAll} className="text-red-400 p-1.5 rounded hover:bg-red-50" title="Clear all" data-testid="chart-clear"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg></button>

          <button onClick={handleSave} disabled={saving} className="font-bold text-white px-5 py-1.5 rounded-lg bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-50 text-[12px] ml-2" data-testid="chart-save">{saving ? 'Saving…' : 'Save to Section'}</button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 ml-1 text-[18px]" data-testid="chart-close">&times;</button>
        </div>

        {legacyEmpty && (
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 flex items-center gap-2" data-testid="chart-legacy-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>This chart was saved without editable data. Start from a template, use <b>AI Generate</b>, or build from scratch — then Save to replace the original.</span>
          </div>
        )}

        {/* AI Prompt modal */}
        {showAIModal && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-[60]" data-testid="chart-ai-modal" onClick={() => !aiLoading && setShowAIModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-[560px] w-full" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0"><path d="M12 2l2.09 5.26L19 9.27l-4.55 3.96L16 19l-4-3-4 3 1.55-5.77L5 9.27l4.91-2.01L12 2z"/></svg>
                  </div>
                  <div>
                    <p className="text-[17px] font-bold text-gray-800">Generate Diagram with AI</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">Describe any workflow, process, or concept — we'll build the flowchart for you.</p>
                  </div>
                </div>
                <button onClick={() => !aiLoading && setShowAIModal(false)} disabled={aiLoading} className="text-gray-400 hover:text-gray-600 disabled:opacity-30"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
              </div>
              <textarea value={aiPrompt} onChange={e => { setAiPrompt(e.target.value); setAiError(''); }} disabled={aiLoading}
                placeholder={'Try:\n• "Student enrollment process from application to admission"\n• "Water cycle in nature"\n• "Decision tree for choosing a programming language"\n• "Software development lifecycle"\n• "Photosynthesis steps"'}
                rows={6} className="w-full text-[13px] px-4 py-3 rounded-xl border-2 border-gray-100 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:bg-white focus:border-violet-300 resize-none transition-all disabled:opacity-60"
                data-testid="chart-ai-prompt" />
              {aiError && <p className="text-[11px] text-red-500 mt-2 flex items-center gap-1.5"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{aiError}</p>}
              <p className="text-[10px] text-gray-400 mt-3">The AI will produce up to 15 shapes with labeled connectors. You can then manually edit each shape, move them, restyle colors, or add more.</p>
              <div className="flex items-center gap-2 mt-4">
                <div className="flex-1" />
                <button onClick={() => setShowAIModal(false)} disabled={aiLoading} className="text-[12px] font-medium text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100 disabled:opacity-50" data-testid="chart-ai-cancel">Cancel</button>
                <button onClick={aiGenerate} disabled={aiLoading || !aiPrompt.trim()} className="text-[12px] font-bold text-white px-5 py-2 rounded-lg transition-all active:scale-95 flex items-center gap-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 shadow disabled:opacity-40 disabled:cursor-not-allowed" data-testid="chart-ai-submit">
                  {aiLoading ? (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="animate-spin"><circle cx="12" cy="12" r="10" strokeDasharray="16 48"/></svg>Generating diagram…</>) : (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2l2.09 5.26L19 9.27l-4.55 3.96L16 19l-4-3-4 3 1.55-5.77L5 9.27l4.91-2.01L12 2z"/></svg>Generate</>)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Templates modal */}
        {showTemplates && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="chart-template-modal">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-[700px] w-full">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[18px] font-bold text-gray-800">Start with a template</p>
                  <p className="text-[12px] text-gray-400 mt-0.5">Pick a layout to begin, AI-generate, or start blank</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setShowTemplates(false); setShowAIModal(true); }} className="text-[12px] font-bold text-white px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 flex items-center gap-1" data-testid="chart-template-ai">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.09 5.26L19 9.27l-4.55 3.96L16 19l-4-3-4 3 1.55-5.77L5 9.27l4.91-2.01L12 2z"/></svg> AI Generate
                  </button>
                  <button onClick={blankStart} className="text-[12px] font-medium text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50" data-testid="chart-template-blank">Start Blank →</button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {TEMPLATES.map(tpl => (
                  <button key={tpl.id} onClick={() => loadTemplate(tpl)} className="rounded-xl border-2 border-gray-100 p-4 hover:border-[#34A853] hover:shadow-lg transition-all group/tpl text-left" data-testid={`chart-template-${tpl.id}`}>
                    <div className="aspect-video rounded-lg bg-gradient-to-br from-[#f0fdf4] to-white mb-3 flex items-center justify-center text-[28px]">
                      {tpl.id === 'flowchart' && '▭→◇'}
                      {tpl.id === 'decision' && '◇⇣'}
                      {tpl.id === 'org' && '👔'}
                      {tpl.id === 'process' && '→→→'}
                      {tpl.id === 'mind' && '⬭✦'}
                    </div>
                    <p className="text-[13px] font-bold text-gray-700 group-hover/tpl:text-[#34A853]">{tpl.label}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{tpl.id === 'flowchart' ? '6 shapes · branching' : tpl.id === 'decision' ? '7 shapes · tree' : tpl.id === 'org' ? '8 shapes · hierarchy' : tpl.id === 'process' ? '5 shapes · linear' : '7 shapes · radial'}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {/* Left: shape palette */}
          <div className="w-[180px] bg-white border-r border-gray-200 overflow-y-auto" data-testid="chart-palette">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Shapes</p>
              <span className="text-[9px] text-gray-300">Click + place</span>
            </div>
            {SHAPE_CATALOG.map(cat => (
              <div key={cat.cat} className="px-2 py-2">
                <p className="text-[9px] font-bold text-gray-300 uppercase tracking-wider mb-1.5 px-1">{cat.cat}</p>
                <div className="grid grid-cols-2 gap-1">
                  {cat.items.map(item => (
                    <button key={item.id} onClick={() => setPlacingShape(item.id)}
                      className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-[9px] font-medium p-1 transition-all active:scale-95 ${placingShape === item.id ? 'bg-[#34A853] text-white border-[#34A853] shadow' : 'bg-white border-gray-100 hover:border-[#34A853]/40 hover:bg-[#34A853]/[0.03] text-gray-600'}`}
                      data-testid={`chart-palette-${item.id}`}>
                      <span className="text-[18px] leading-none mb-0.5">{item.icon}</span>
                      <span className="text-[8px] leading-tight">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="px-3 py-2 border-t border-gray-100 mt-2">
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Theme</p>
              <div className="grid grid-cols-5 gap-1">
                {Object.keys(THEMES).map(tid => (
                  <button key={tid} onClick={() => applyTheme(tid)} className={`h-6 rounded border-2 transition-all ${theme === tid ? 'scale-110 shadow' : 'border-transparent'}`} style={{ background: THEMES[tid].stroke }} title={tid} data-testid={`chart-theme-${tid}`} />
                ))}
              </div>
            </div>
            <div className="px-3 py-2 border-t border-gray-100">
              <button onClick={() => setShowTemplates(true)} className="w-full text-[10px] font-bold text-[#34A853] bg-[#34A853]/10 hover:bg-[#34A853]/20 rounded-lg py-2 flex items-center justify-center gap-1" data-testid="chart-open-templates">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                Templates
              </button>
            </div>
          </div>

          {/* Center: canvas */}
          <div className="flex-1 overflow-auto bg-[#f5f5f5] flex items-center justify-center p-4 relative" style={{ cursor: placingShape ? 'crosshair' : drawingConn ? 'crosshair' : 'default' }}>
            <div className="relative shadow-xl bg-white rounded-md" style={{ width: CW, minWidth: CW }}>
              {showGrid && <div className="absolute inset-0 pointer-events-none rounded-md" style={{ backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)', backgroundSize: `${GRID}px ${GRID}px` }} />}
              <Stage ref={stageRef} width={CW} height={CH} onMouseDown={onStageDown} onMouseMove={onStageMove} onMouseUp={onStageUp} data-testid="chart-canvas">
                <Layer>
                  <Rect width={CW} height={CH} fill="transparent" name="bg" />
                  {connectors.map(renderConn)}
                  {nodes.map(node => {
                    const isSel = selectedId === node.id;
                    const isEdit = editingId === node.id;
                    return (
                      <Group key={node.id} id={node.id} name="node-group" x={node.x} y={node.y} width={node.width} height={node.height}
                        draggable={!isEdit}
                        onDragMove={e => onNodeDragMove(node.id, e)}
                        onDragEnd={e => onNodeDragEnd(node.id, e)}
                        onClick={() => { setSelectedId(node.id); setSelectedConnId(null); }}
                        onTap={() => { setSelectedId(node.id); setSelectedConnId(null); }}
                        onDblClick={() => startTextEdit(node.id)}
                        onDblTap={() => startTextEdit(node.id)}
                        onMouseEnter={() => setHoveredNodeId(node.id)}
                        onMouseLeave={() => setHoveredNodeId(prev => prev === node.id ? null : prev)}
                        onTransformEnd={e => onNodeTransformEnd(node.id, e)}>
                        {renderNodeShape(node, isSel)}
                        {!isEdit && <Text x={6} y={6} width={node.width - 12} height={node.height - 12} text={node.text || ''} align="center" verticalAlign="middle" fontSize={node.fontSize || 14} fontStyle="500" fill={node.textColor || '#1e293b'} wrap="word" ellipsis listening={false} />}
                      </Group>
                    );
                  })}
                  {/* anchors for selected OR hovered node (easier connector discovery) */}
                  {!editingId && !drawingConn && (() => {
                    const nd = nodes.find(n => n.id === selectedId) || nodes.find(n => n.id === hoveredNodeId);
                    if (!nd) return null;
                    return renderAnchors(nd, hoveredNodeId === nd.id);
                  })()}
                  {/* In-progress connector drawing */}
                  {drawingConn && (
                    <Arrow points={[drawingConn.x1, drawingConn.y1, drawingConn.x2, drawingConn.y2]} stroke="#34A853" strokeWidth={2} fill="#34A853" dash={[6, 4]} pointerLength={10} pointerWidth={10} listening={false} />
                  )}
                  <Transformer ref={trRef} rotateEnabled={false} boundBoxFunc={(o, n) => ({ ...n, width: Math.max(40, n.width), height: Math.max(30, n.height) })} />
                </Layer>
              </Stage>
              {editingId && editingNode && (() => {
                const stageEl = stageRef.current?.container();
                if (!stageEl) return null;
                const sb = stageEl.getBoundingClientRect();
                const sx = sb.width / CW, sy = sb.height / CH;
                return (<textarea ref={textAreaRef} value={editingNode.text || ''} onChange={e => {
                  const v = e.target.value;
                  setNodes(prev => prev.map(n => n.id === editingId ? { ...n, text: v } : n));
                }} onKeyDown={e => { if (e.key === 'Escape') commitTextEdit(); }} onBlur={commitTextEdit}
                  className="absolute z-20 border-2 border-blue-400 bg-white/95 outline-none resize-none p-1 shadow-lg rounded text-center"
                  style={{
                    left: editingNode.x * sx + 6, top: editingNode.y * sy + 6,
                    width: (editingNode.width - 12) * sx, height: (editingNode.height - 12) * sy,
                    fontSize: Math.max(10, (editingNode.fontSize || 14) * sy), color: editingNode.textColor,
                    fontFamily: 'inherit', lineHeight: 1.3,
                  }} data-testid="chart-text-input" autoFocus />);
              })()}
            </div>
          </div>

          {/* Right: Properties Panel */}
          <div className="w-[240px] bg-white border-l border-gray-200 overflow-y-auto" data-testid="chart-props-panel">
            {!selectedNode && !selectedConn && (
              <div className="p-4 text-center">
                <p className="text-[10px] text-gray-400">Click a shape or connector to edit</p>
                <div className="mt-4 space-y-2 text-[10px] text-gray-400 text-left">
                  <p className="font-bold text-gray-500">💡 Tips</p>
                  <p>· Click a shape in left palette then click canvas to place</p>
                  <p>· <b className="text-gray-500">Hover or select</b> a shape → drag its green dots to another shape to connect</p>
                  <p>· Double-click to rename</p>
                  <p>· Delete key to remove</p>
                  <p>· Ctrl+Z / Ctrl+Y for undo/redo</p>
                  <p>· <b className="text-violet-600">AI Generate</b> above builds a full diagram from your description</p>
                </div>
              </div>
            )}
            {selectedNode && (
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Shape</p>
                  <p className="text-[12px] font-semibold text-gray-700 capitalize">{selectedNode.type}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Text</p>
                  <textarea value={selectedNode.text || ''} onChange={e => updateNodeProp('text', e.target.value)} rows={2} className="w-full text-[12px] px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:bg-white resize-none" data-testid="chart-prop-text" />
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Colors</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="color" value={selectedNode.fill} onChange={e => updateNodeProp('fill', e.target.value)} className="w-5 h-5 rounded cursor-pointer p-0 border-0" data-testid="chart-prop-fill" />Fill</label>
                    <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="color" value={selectedNode.stroke} onChange={e => updateNodeProp('stroke', e.target.value)} className="w-5 h-5 rounded cursor-pointer p-0 border-0" data-testid="chart-prop-stroke" />Line</label>
                    <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="color" value={selectedNode.textColor} onChange={e => updateNodeProp('textColor', e.target.value)} className="w-5 h-5 rounded cursor-pointer p-0 border-0" data-testid="chart-prop-textcolor" />Text</label>
                  </div>
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Font Size</p>
                  <input type="range" min={8} max={32} value={selectedNode.fontSize || 14} onChange={e => updateNodeProp('fontSize', +e.target.value)} className="w-full accent-[#34A853]" data-testid="chart-prop-fontsize" />
                  <p className="text-[9px] text-gray-400 text-right">{selectedNode.fontSize || 14}px</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Border Width</p>
                  <input type="range" min={1} max={6} value={selectedNode.strokeWidth || 2} onChange={e => updateNodeProp('strokeWidth', +e.target.value)} className="w-full accent-[#34A853]" data-testid="chart-prop-strokewidth" />
                  <p className="text-[9px] text-gray-400 text-right">{selectedNode.strokeWidth || 2}px</p>
                </div>
                <button onClick={deleteSelected} className="w-full text-[11px] font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg py-2 transition-colors active:scale-95" data-testid="chart-delete-node">Delete Shape</button>
              </div>
            )}
            {selectedConn && (
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Connector</p>
                  <p className="text-[11px] text-gray-400">{selectedConn.fromSide} → {selectedConn.toSide}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Label</p>
                  <input value={selectedConn.label || ''} onChange={e => {
                    const v = e.target.value;
                    const nc = connectors.map(c => c.id === selectedConn.id ? { ...c, label: v } : c);
                    setConnectors(nc);
                  }} onBlur={() => snapshot(nodes, connectors)} placeholder="Yes / No / …" className="w-full text-[12px] px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:bg-white" data-testid="chart-prop-connlabel" />
                </div>
                <button onClick={deleteSelected} className="w-full text-[11px] font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg py-2 transition-colors active:scale-95" data-testid="chart-delete-conn">Delete Connector</button>
              </div>
            )}
          </div>
        </div>

        {/* Status bar */}
        <div className="flex items-center gap-3 px-3 py-1 bg-white border-t border-gray-200 text-[9px] text-gray-400">
          <span>{CW}×{CH}</span>
          <span>Cursor: {cursor.x}, {cursor.y}</span>
          <span>{nodes.length} shape{nodes.length !== 1 ? 's' : ''} · {connectors.length} connector{connectors.length !== 1 ? 's' : ''}</span>
          {placingShape && <span className="text-[#34A853] font-semibold">Placing: {placingShape} · click to drop, Esc to cancel</span>}
          {drawingConn && <span className="text-[#34A853] font-semibold">Drawing connector · drop on another shape</span>}
          <div className="flex-1" />
          <span>Double-click to rename · Drag dots to connect · Del to remove</span>
        </div>
      </div>
    </div>
  );
}
